import { inject } from '@angular/core';
import { tap } from 'rxjs';
import { patchState, signalStore, withHooks, withMethods } from '@ngrx/signals';
import { updateEntity, withEntities } from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';
import { withAsyncJob } from '@dgacontrol-frontend/shared-util-polling';

import { CatchmentWorkDataFacadeService } from './catchment-work-data-facade.service';
import type {
  CatchmentWorkDataV4,
  CatchmentWorkDataV4MeasurementsListParams,
  ExportJobCreateRequestV4Request,
  ExportJobV4,
} from './generated/types';

/** `sessionStorage` key holding the id of the export this TAB left in flight. */
const EXPORT_JOB_KEY = 'dgacontrol.exportJob';

/**
 * Single dual-app store for the v4 `measurements` listing, shared by the web table
 * (`dt-catchment-work-data`) and the admin logs table (`dt-catchment-work-data-logs`).
 *
 * The backend consolidated `measurements/` + `measurements/counts/` into ONE token-scoped
 * endpoint (`catchmentWorkDataV4MeasurementsList`), so both apps hit the SAME operation with
 * different optional query params — there is no longer a separate `WithCounts` shape. This is
 * the `nodes.store.ts` pattern: one store, one `withEntities<CatchmentWorkDataV4>`, and the
 * app-specific mutation methods coexist (web triggers send/undo/export; admin triggers
 * send/reset-retries-dga/reset-retries-node/lora).
 *
 * `load`/`loadMore` + the cursor state (`nextCursor`/`previousCursor`/`isLoading`/`error`) come
 * from the shared `withCursorPagination` signalStoreFeature. The cursor is an OPAQUE token the
 * feature extracts from the response's `next`/`previous`; the generated client + the
 * `ApiBaseUrlInterceptor` resolve the base from `environment`.
 *
 * NOT a cache: it's a parameterized live query, so there is no `onInit` and no `loaded` flag —
 * the component drives `load(params)` (web passes `catchment_work` as the obra identity + date
 * filters; admin passes the 15 discrete filters). The signals payoff is reactivity (mutations
 * reflect without a reload), not request dedup.
 *
 * Mutations each return an Observable the component subscribes to; the error propagates so the
 * global `httpErrorInterceptor` (F1) toasts it — the store never sets `error` from a mutation.
 */
export const CatchmentWorkDataStore = signalStore(
  withEntities<CatchmentWorkDataV4>(),
  withCursorPagination<
    CatchmentWorkDataV4,
    CatchmentWorkDataV4MeasurementsListParams
  >(() => {
    const facade = inject(CatchmentWorkDataFacadeService);
    return (query, cursor) =>
      facade.getMeasurements({ ...query, cursor: cursor ?? undefined });
  }),
  // xlsx export: the backend enqueues a job and the frontend watches it. Lives HERE and
  // not in the component because the store is provided on the authenticated route shell,
  // so the watch survives closing the modal and navigating the menu, and dies at logout.
  // Adds `job`/`jobRunning`/`jobError` + `startJob`/`resumeJob`/`clearJob`.
  withAsyncJob<ExportJobCreateRequestV4Request, ExportJobV4>(() => {
    const facade = inject(CatchmentWorkDataFacadeService);
    return {
      start: (body) => facade.requestExport(body),
      poll: (jobId) => facade.getExportJob(jobId),
      jobIdOf: (job) => job.job_id,
      // `pending` + `processing` are the CLOSED list of in-progress states; anything
      // else is terminal. Enumerating the terminal ones instead would hang the loop the
      // day the backend adds one.
      done: (job) => job.status !== 'pending' && job.status !== 'processing',
      // First read is immediate, then 2s → 3s → 5s → 8s (the last repeats): ~35 reads in
      // the worst case, ~5 for a job that takes 15s.
      delays: [2000, 3000, 5000, 8000],
      timeout: 300_000,
      persistKey: EXPORT_JOB_KEY,
    };
  }),
  withMethods((store, facade = inject(CatchmentWorkDataFacadeService)) => ({
    /** Flag the measurement as sent to DGA; the response is the post-send row → patch from it (web). */
    sendToDga(identity: string, id: number) {
      return facade
        .sendToDgaV4(identity)
        .pipe(
          tap((updated) =>
            { patchState(store, updateEntity({ id, changes: updated })); },
          ),
        );
    },

    /** Undo the send-to-DGA flag; the response is the reverted row → patch from it (web). */
    undoSendToDga(identity: string, id: number) {
      return facade
        .undoSendToDgaV4(identity)
        .pipe(
          tap((updated) =>
            { patchState(store, updateEntity({ id, changes: updated })); },
          ),
        );
    },

    /** Reset the DGA retry counter; PATCH returns the full row → patch from the response (admin). */
    resetRetriesDga(identity: string, id: number) {
      return facade
        .patchMeasurementRetriesDga(identity)
        .pipe(
          tap((updated) =>
            { patchState(store, updateEntity({ id, changes: updated })); },
          ),
        );
    },

    /**
     * Reset node-send retries (admin). The backend now returns the FULL affected measurement
     * (it used to return the audit log), so we patch the row from the authoritative response.
     * This keeps the table cell AND the detail drawer reactive (no reload, no re-click).
     */
    resetRetriesNode(identity: string, id: number) {
      return facade
        .resetRetriesNodeV4(identity)
        .pipe(
          tap((updated) =>
            { patchState(store, updateEntity({ id, changes: updated })); },
          ),
        );
    },

    /** Trigger a LoRa downlink to fetch pending data; no row change (admin). */
    sendLoraDownlink(identity: string) {
      return facade.sendLoraDownlinkV4(identity);
    },

  })),
  withHooks({
    // method-shorthand (NEVER an arrow → TS7006 "implicit any" on a clean compile).
    onInit(store) {
      // The ONLY thing this store does on init. Still not a cache — this re-attaches to
      // an export the tab already had in flight (after an F5); with nothing persisted it
      // is a no-op, and a job that is gone answers 404 and is dropped silently.
      store.resumeJob();
    },
  }),
);
